(() => {
  const selectors = {
    teaser: "[data-bundle-teaser]",
    open: "[data-bundle-open]",
    modal: "[data-bundle-modal]",
    close: "[data-bundle-close]",
    step: "[data-step]",
    stepOptions: "[data-step-options]",
    progress: "[data-bundle-progress]",
    add: "[data-bundle-add]",
    status: "[data-bundle-status]",
    suggestions: "[data-bundle-suggestions]",
    preview: "[data-bundle-preview]",
    confirm: "[data-bundle-confirm]",
    confirmCancel: "[data-bundle-confirm-cancel]",
    confirmConfirm: "[data-bundle-confirm-confirm]",
    previewCarousel: "[data-bundle-preview-carousel]",
  };

  const init = () => {
    document.querySelectorAll(selectors.teaser).forEach((teaser) => {
      const modal = teaser.querySelector(selectors.modal);
      if (!modal) return;
      const configEl = modal.querySelector("[data-bundle-config]");
      const baseConfig = configEl ? JSON.parse(configEl.textContent) : {};
      const endpoint = teaser.dataset.appEndpoint;

      const state = {
        ...baseConfig,
        items: {
          fabric: [],
          mordant: [],
          dye: [],
        },
        selections: {
          fabric: new Set(),
          mordant: new Set(),
          dye: new Set(),
        },
        selectionOrder: {
          fabric: [],
          mordant: [],
          dye: [],
        },
        texts: {
          validationFabric: modal.dataset.validationFabric || "Please choose your fabric.",
          validationMordant: modal.dataset.validationMordant || "Please choose your mordant.",
          validationDye: modal.dataset.validationDye || "Please choose your color.",
          discountLabel: modal.dataset.discountLabel || "Bundled as a complete starting set",
          discountNoteTemplate:
            modal.dataset.discountNoteTemplate || "Includes [discount_pct]% automatic adjustment.",
        },
        instructionVariantId: baseConfig.instructionVariantId || "",
      };

      const openBtn = teaser.querySelector(selectors.open);
      const closeBtns = modal.querySelectorAll(selectors.close);
      const addBtn = modal.querySelector(selectors.add);
      const statusEl = modal.querySelector(selectors.status);
      const progressEl = modal.querySelector(selectors.progress);
      const previewBtn = modal.querySelector(selectors.preview);
      const confirmEl = modal.querySelector(selectors.confirm);
      const confirmCancelBtn = modal.querySelector(selectors.confirmCancel);
      const confirmConfirmBtn = modal.querySelector(selectors.confirmConfirm);
      const confetti = modal.querySelector(".bundle-confetti");
      const subtotalEl = modal.querySelector("[data-bundle-subtotal]");
      const discountEl = modal.querySelector("[data-bundle-discount]");
      const totalEl = modal.querySelector("[data-bundle-total]");
      const compareEl = modal.querySelector("[data-bundle-compare]");
      const discountBadge = modal.querySelector(".bundle-modal__discount");
      const discountNoteEl = modal.querySelector("[data-bundle-discount-note]");
      const modalBody = modal.querySelector(".bundle-modal__body");
      const modalDialog = modal.querySelector(".bundle-modal__dialog");

      let lastFocused;

      const openModal = async () => {
        lastFocused = document.activeElement;
        modal.classList.add("is-open");
        modal.setAttribute("aria-hidden", "false");
        const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
        document.body.dataset.bundleScroll = String(scrollY);
        document.body.style.position = "fixed";
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = "100%";
        document.body.style.overflow = "hidden";
        document.documentElement.style.overflow = "hidden";
        document.documentElement.classList.add("bundle-modal-open");
        document.body.classList.add("bundle-modal-open");
        trapFocus(modal);
        pushEvent("modal_opened", state);
        ensureModalWheel(modal, modalBody);

        if (endpoint) {
          try {
            const data = await fetch(endpoint, { credentials: "include" }).then((r) => r.json());
            state.items = data.items || state.items;
            state.rules = data.rules || state.rules;
            state.bundleGroupKey = data.bundleGroupKey || state.bundleGroupKey;
            state.discountPercent = data.discountPercent || state.discountPercent;
            state.instructionVariantId = data.instructionVariantId || state.instructionVariantId;
            restoreSelections(state);
            renderSteps(modal, state, statusEl);
            renderSuggestions(modal, data.suggestions || []);
            renderPreviewBundles(teaser, data.suggestions || [], state);
            updateTotals(state, subtotalEl, discountEl, totalEl, compareEl, discountNoteEl);
            updateModalTone(modalDialog, state);
            if (discountBadge) {
              discountBadge.textContent = state.texts.discountLabel;
            }
          } catch (error) {
            console.error("Bundle builder fetch failed", error);
          }
        } else {
          restoreSelections(state);
          renderSteps(modal, state, statusEl);
          updateTotals(state, subtotalEl, discountEl, totalEl, compareEl, discountNoteEl);
          updateModalTone(modalDialog, state);
        }
      };

      const closeModal = () => {
        modal.classList.remove("is-open");
        modal.setAttribute("aria-hidden", "true");
        const scrollY = Number(document.body.dataset.bundleScroll || 0);
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        document.body.style.overflow = "";
        document.documentElement.style.overflow = "";
        document.documentElement.classList.remove("bundle-modal-open");
        document.body.classList.remove("bundle-modal-open");
        delete document.body.dataset.bundleScroll;
        const prevScrollBehavior = document.documentElement.style.scrollBehavior;
        document.documentElement.style.scrollBehavior = "auto";
        window.scrollTo(0, scrollY);
        requestAnimationFrame(() => {
          document.documentElement.style.scrollBehavior = prevScrollBehavior;
        });
        releaseFocus(modal);
        persistSelections(state);
        if (lastFocused) lastFocused.focus();
      };

      openBtn?.addEventListener("click", openModal);
      closeBtns.forEach((btn) => btn.addEventListener("click", closeModal));
      modal.addEventListener("keydown", (event) => {
        if (event.key === "Escape") closeModal();
      });

      previewBtn?.addEventListener("click", () => {
        pushEvent("bundle_preview_clicked", state);
        statusEl.textContent = "Previewing selection in cart summary.";
      });

      addBtn?.addEventListener("click", async () => {
        if (addBtn?.dataset.added === "true") {
          const cartDrawer = document.querySelector("cart-drawer-component");
          cartDrawer?.open?.();
          return;
        }
        const validation = validateSelections(state);
        if (!validation.valid) {
          statusEl.textContent = validation.message;
          statusEl.setAttribute("role", "alert");
          return;
        }

        const unavailable = findUnavailableSelections(state);
        if (unavailable.length) {
          statusEl.textContent = "Some items in your bundle are sold out. Please update your selection.";
          statusEl.setAttribute("role", "alert");
          return;
        }

        const items = collectCartItems(state);
        if (!items.length) {
          statusEl.textContent = "We couldn't add your bundle. Please try again.";
          statusEl.setAttribute("role", "alert");
          return;
        }
        const signature = getSelectionSignature(state);
        const lastSignature = readLastAddedSignature(state);
        if (lastSignature && lastSignature === signature) {
          const confirmed = await confirmDuplicateAdd(confirmEl, confirmConfirmBtn, confirmCancelBtn);
          if (!confirmed) return;
        }

        setAddButtonState(addBtn, true);
        try {
          const response = await fetch("/cart/add.js", {
            method: "POST",
            credentials: "same-origin",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({ items }),
          });
          if (!response.ok) {
            let message = "We couldn't add your bundle. Please try again.";
            try {
              const errorData = await response.json();
              message = errorData?.description || errorData?.message || errorData?.errors || message;
            } catch (_) {
              // ignore JSON parse errors
            }
            throw new Error(message);
          }
          statusEl.textContent = "Set added. View cart when ready.";
          setAddButtonAdded(addBtn, addBtn?.dataset.addedText || "View cart");
          confetti?.classList.add("is-active");
          setTimeout(() => confetti?.classList.remove("is-active"), 800);
          persistSelections(state);
          writeLastAddedSignature(state, signature);
          pushEvent("bundle_added", state, items);
          pushEvent("bundle_discount_applied", state, items);
          await refreshCartUi(items);
        } catch (error) {
          statusEl.textContent = error?.message || "We couldn't add your bundle. Please try again.";
          statusEl.setAttribute("role", "alert");
        } finally {
          setAddButtonState(addBtn, false);
          updateActionState(modal, state, statusEl);
        }
      });

      renderSteps(modal, state, statusEl);
      updateActionState(modal, state, statusEl);
      updateTotals(state, subtotalEl, discountEl, totalEl, compareEl, discountNoteEl);
      updateModalTone(modalDialog, state);
    });
  };

  const smoothWheelState = new WeakMap();

  const ensureModalWheel = (modal, modalBody) => {
    if (!modalBody || modalBody.dataset.wheelBound === "true") return;
    modalBody.addEventListener(
      "wheel",
      (event) => {
        if (!modal.classList.contains("is-open")) return;
        if (modalBody.scrollHeight <= modalBody.clientHeight) return;
        event.preventDefault();
        let delta = event.deltaY;
        if (event.deltaMode === 1) delta *= 16;
        if (event.deltaMode === 2) delta *= modalBody.clientHeight;
        delta = Math.max(-140, Math.min(140, delta));

        let state = smoothWheelState.get(modalBody);
        if (!state) {
          state = { target: modalBody.scrollTop, raf: null };
          smoothWheelState.set(modalBody, state);
        }
        const maxScroll = modalBody.scrollHeight - modalBody.clientHeight;
        state.target = Math.max(0, Math.min(maxScroll, state.target + delta));

        if (!state.raf) {
          const step = () => {
            const current = modalBody.scrollTop;
            const distance = state.target - current;
            if (Math.abs(distance) < 0.5) {
              modalBody.scrollTop = state.target;
              state.raf = null;
              return;
            }
            modalBody.scrollTop = current + distance * 0.12;
            state.raf = requestAnimationFrame(step);
          };
          state.raf = requestAnimationFrame(step);
        }
      },
      { passive: false }
    );
    modalBody.dataset.wheelBound = "true";
  };

  const renderSteps = (modal, state, statusEl) => {
    modal.querySelectorAll(selectors.step).forEach((stepEl) => {
      const stepType = stepEl.dataset.step;
      const configuredMax = Number(state?.rules?.[stepType]);
      const fallbackMax = Number(stepEl.dataset.max || 1);
      const max = Number.isFinite(configuredMax) && configuredMax > 0 ? configuredMax : fallbackMax;
      const container = stepEl.querySelector(selectors.stepOptions);
      container.innerHTML = "";

      const items = state.items[stepType] || [];
      if (!items.length) {
        container.innerHTML = "";
        for (let i = 0; i < 3; i += 1) {
          const skeleton = document.createElement("div");
          skeleton.className = "bundle-option bundle-option--skeleton";
          skeleton.setAttribute("aria-hidden", "true");
          container.appendChild(skeleton);
        }
        return;
      }

      items.forEach((item) => {
        const option = document.createElement("button");
        option.type = "button";
        option.className = "bundle-option";
        option.setAttribute("data-variant-id", item.variantId);
        if (stepType === "dye") {
          option.classList.add("bundle-option--dye");
          option.dataset.dyeColorNormal = item.dyeColor || "";
          option.dataset.dyeColorFerrous = item.dyeFerrousColor || "";
          option.dataset.dyeColorLactate = item.dyeLactateColor || "";
          const tone = getActiveMordantTone(state);
          const displayColor = getDyeColorForTone(item, tone);
          if (displayColor) option.style.setProperty("--dye-color", displayColor);
        }
        const rawPrice = getItemPrice(item);
        const discountPct = Number(state.discountPercent || 0) / 100;
        const discounted = rawPrice - rawPrice * discountPct;
        const compareHtml = discountPct > 0 && rawPrice > 0
          ? `<span class="bundle-option__price-compare">${formatMoney(rawPrice)}</span>`
          : "";
        const finalDisplay = rawPrice > 0 ? formatMoney(discounted) : "—";
        const note = item.mordantNote ? `<div class="bundle-option__note">${item.mordantNote}</div>` : "";
        const toneLabel = item.mordantTone ? formatToneLabel(item.mordantTone) : "";
        const tone = item.mordantTone
          ? `<div class="bundle-option__tone" data-tone="${item.mordantTone}">${toneLabel}</div>`
          : "";
        const swatch = item.dyeColor
          ? `<span class="bundle-option__swatch" style="--swatch-color: ${item.dyeColor}"></span>`
          : "";
        option.innerHTML = `
          <img class="bundle-option__image" src="${item.image || ""}" alt="${item.title}">
          <div class="bundle-option__title">${item.title}</div>
          ${swatch}
          <div class="bundle-option__prices">
            <span class="bundle-option__price-final">${finalDisplay}</span>
            ${compareHtml}
          </div>
          ${tone}
          ${note}
        `;

        if (state.selections[stepType]?.has(item.variantId)) {
          option.classList.add("is-selected");
        }

        option.addEventListener("click", () => {
          const { changed, removedId } = toggleSelection(state, stepType, item.variantId, max);
          if (!changed && statusEl) {
            statusEl.textContent = `Please choose only ${max} ${stepType}(s).`;
          }
          if (removedId && removedId !== item.variantId) {
            const removedEl = container.querySelector(`[data-variant-id="${removedId}"]`);
            removedEl?.classList.remove("is-selected");
          }
          option.classList.toggle("is-selected", state.selections[stepType].has(item.variantId));
          updateProgress(modal, state);
          updateTotals(state,
            modal.querySelector("[data-bundle-subtotal]"),
            modal.querySelector("[data-bundle-discount]"),
            modal.querySelector("[data-bundle-total]"),
            modal.querySelector("[data-bundle-compare]"),
            modal.querySelector("[data-bundle-discount-note]")
          );
          updateModalTone(modal.querySelector(".bundle-modal__dialog"), state);
          updateDyeOptionColors(modal, state);
          updateActionState(modal, state, statusEl);
          persistSelections(state);
        });

        container.appendChild(option);
      });
    });

    updateDyeOptionColors(modal, state);
    updateProgress(modal, state);
    updateActionState(modal, state, statusEl);
  };

  const renderSuggestions = (modal, suggestions) => {
    const list = modal.querySelector(selectors.suggestions);
    if (!list) return;
    list.innerHTML = "";
    suggestions.forEach((suggestion) => {
      const card = document.createElement("button");
      card.type = "button";
      card.className = "bundle-suggestion";
      card.textContent = suggestion.title || "Suggested bundle";
      card.addEventListener("click", () => pushEvent("bundle_preview_clicked", suggestion));
      list.appendChild(card);
    });
  };

  const renderPreviewBundles = (teaser, suggestions, state) => {
    const carousel = teaser.querySelector(selectors.previewCarousel);
    if (!carousel || !Array.isArray(suggestions) || !suggestions.length) return;

    const itemsByVariant = buildItemMap(state.items);
    const discountPct = Number(state.discountPercent || 0) / 100;
    const cards = suggestions
      .map((suggestion) => buildPreviewCard(suggestion, itemsByVariant, discountPct))
      .filter(Boolean);

    if (!cards.length) return;
    carousel.innerHTML = "";
    cards.forEach((card) => carousel.appendChild(card));
    carousel.dataset.dynamic = "true";
  };

  const toggleSelection = (state, stepType, variantId, max) => {
    const set = state.selections[stepType];
    const order = state.selectionOrder[stepType] || [];
    if (set.has(variantId)) {
      set.delete(variantId);
      state.selectionOrder[stepType] = order.filter((id) => id !== variantId);
      return { changed: true, removedId: variantId };
    }

    let removedId = null;
    if (set.size >= max) {
      removedId = order[order.length - 1] || Array.from(set)[set.size - 1];
      if (removedId) {
        set.delete(removedId);
      }
      state.selectionOrder[stepType] = order.filter((id) => id !== removedId);
    }

    set.add(variantId);
    state.selectionOrder[stepType] = [...state.selectionOrder[stepType], variantId];
    return { changed: true, removedId };
  };

  const buildItemMap = (itemsByType) => {
    const map = {};
    Object.values(itemsByType || {}).forEach((items) => {
      (items || []).forEach((item) => {
        if (item?.variantId) map[item.variantId] = item;
      });
    });
    return map;
  };

  const normalizeSuggestionItems = (suggestion, itemsByVariant) => {
    if (!suggestion) return [];
    if (Array.isArray(suggestion.items)) return suggestion.items;
    if (suggestion.items && typeof suggestion.items === "object") {
      return Object.values(suggestion.items).flat().filter(Boolean);
    }
    if (Array.isArray(suggestion.variantIds)) {
      return suggestion.variantIds.map((id) => itemsByVariant[id]).filter(Boolean);
    }
    if (Array.isArray(suggestion.variants)) {
      return suggestion.variants.map((id) => itemsByVariant[id] || id).filter(Boolean);
    }
    return [];
  };

  const buildPreviewCard = (suggestion, itemsByVariant, discountPct) => {
    const items = normalizeSuggestionItems(suggestion, itemsByVariant);
    if (!items.length) return null;

    const subtotal = items.reduce((sum, item) => sum + getItemPrice(item), 0);
    const discount = subtotal * discountPct;
    const total = subtotal - discount;
    const title = suggestion.title || "Bundle idea";
    const badge = suggestion.badge || suggestion.tag || "";

    const card = document.createElement("article");
    card.className = "bundle-card";
    card.innerHTML = `
      ${badge ? `<span class="bundle-card__badge" aria-label="${badge} badge">${badge}</span>` : ""}
      <div class="bundle-card__body">
        <h3 class="bundle-card__title">${title}</h3>
        ${suggestion.description ? `<p class="bundle-card__desc">${suggestion.description}</p>` : ""}
        <div class="bundle-card__items" role="list">
          ${items
            .map((item) => {
              const itemTitle = item?.title || "Item";
              const variantTitle = item?.variantTitle || item?.variant || "";
              return `
                <div class="bundle-card__item" role="listitem">
                  <span class="bundle-card__item-dot" aria-hidden="true"></span>
                  <span class="bundle-card__item-title">${itemTitle}</span>
                  ${variantTitle ? `<span class="bundle-card__item-variant">${variantTitle}</span>` : ""}
                </div>
              `;
            })
            .join("")}
        </div>
        <div class="bundle-card__meta">
          <span class="bundle-card__price">
            ${subtotal > 0 ? formatMoney(total) : "—"}
            ${discount > 0 ? `<span class="bundle-card__price-compare">${formatMoney(subtotal)}</span>` : ""}
          </span>
          <span class="bundle-card__pill">Best value</span>
        </div>
      </div>
    `;

    return card;
  };

  const updateProgress = (modal, state) => {
    const progressEl = modal.querySelector(selectors.progress);
    const statusEl = modal.querySelector(selectors.status);
    const total = Object.keys(state.rules || {}).length;
    const completed = Object.keys(state.rules || {}).filter((type) => {
      return (state.selections[type]?.size || 0) >= state.rules[type];
    }).length;
    const pct = total ? Math.round((completed / total) * 100) : 0;
    if (progressEl) progressEl.style.width = `${pct}%`;
    if (statusEl) {
      statusEl.textContent = "";
    }
    pushEvent("step_completed", state, { completed, total });
  };

  const setAddButtonState = (button, isBusy) => {
    if (!button) return;
    if (!button.dataset.defaultText) {
      button.dataset.defaultText = button.textContent || "Continue with this set.";
    }
    if (isBusy) {
      button.dataset.isBusy = "true";
      button.setAttribute("aria-busy", "true");
      button.disabled = true;
      button.textContent = button.dataset.loadingText || "Adding set…";
      return;
    }
    button.dataset.isBusy = "false";
    button.removeAttribute("aria-busy");
    if (button.dataset.lockedText !== "true") {
      button.textContent = button.dataset.defaultText;
    }
    button.disabled = false;
  };

  const setAddButtonAdded = (button, text) => {
    if (!button) return;
    if (!button.dataset.defaultText) {
      button.dataset.defaultText = button.textContent || "Continue with this set.";
    }
    button.dataset.lockedText = "true";
    button.dataset.added = "true";
    button.textContent = text;
  };

  const resetAddButtonIfNeeded = (modal, state) => {
    const addBtn = modal.querySelector(selectors.add);
    if (!addBtn) return;
    const signature = getSelectionSignature(state);
    const lastSignature = readLastAddedSignature(state);
    if (!lastSignature || lastSignature !== signature) {
      addBtn.dataset.lockedText = "false";
      addBtn.dataset.added = "false";
      addBtn.textContent = addBtn.dataset.defaultText || "Continue with this set.";
    }
  };

  const updateActionState = (modal, state, statusEl) => {
    const addBtn = modal.querySelector(selectors.add);
    const previewBtn = modal.querySelector(selectors.preview);
    if (!addBtn) return;
    const validation = validateSelections(state);
    const ready = validation.valid;
    if (!ready && statusEl) statusEl.setAttribute("role", "status");
    addBtn.disabled = !ready || addBtn.dataset.isBusy === "true";
    addBtn.setAttribute("aria-disabled", String(!ready));
    if (previewBtn) {
      previewBtn.disabled = !ready;
      previewBtn.setAttribute("aria-disabled", String(!ready));
    }
    resetAddButtonIfNeeded(modal, state);
  };

  const formatMoney = (value) => {
    if (value === undefined || value === null || value === "") return "";
    const number = Number(value);
    if (Number.isNaN(number)) return String(value);
    const currency = window.Shopify?.currency?.active || "USD";
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(number);
  };

  const toNumber = (value) => {
    if (value === undefined || value === null) return 0;
    if (typeof value === "object") {
      const candidate = value.amount ?? value.value ?? value.price ?? value?.amountValue;
      if (candidate !== undefined) return toNumber(candidate);
      return 0;
    }
    const cleaned = String(value).replace(/[^0-9.]/g, "");
    const number = Number(cleaned);
    return Number.isNaN(number) ? 0 : number;
  };

  const getItemPrice = (item) => {
    if (!item) return 0;
    return toNumber(item.price ?? item.priceAmount ?? item.amount ?? item?.price?.amount);
  };

  const updateTotals = (state, subtotalEl, discountEl, totalEl, compareEl, discountNoteEl) => {
    if (!totalEl || !compareEl) return;
    const selectedItems = collectSelectedItems(state);
    const subtotal = selectedItems.reduce((sum, item) => sum + getItemPrice(item), 0);
    const discountPct = Number(state.discountPercent || 0) / 100;
    const discount = subtotal * discountPct;
    const total = subtotal - discount;
    if (selectedItems.length && subtotal <= 0) {
      totalEl.textContent = "—";
      compareEl.textContent = "";
      return;
    }
    totalEl.textContent = formatMoney(total);
    compareEl.textContent = discount > 0 ? formatMoney(subtotal) : "";
    if (discountNoteEl) {
      const pct = Number(state.discountPercent || 0);
      const template = state.texts?.discountNoteTemplate || "Includes [discount_pct]% automatic adjustment.";
      discountNoteEl.textContent = pct > 0 ? template.replace("[discount_pct]", String(pct)) : "";
    }
  };

  const isItemAvailable = (item) => {
    if (!item) return true;
    if (item.available === false) return false;
    if (item.availableForSale === false) return false;
    if (typeof item.quantityAvailable === "number" && item.quantityAvailable <= 0) return false;
    if (typeof item.inventory === "number" && item.inventory <= 0) return false;
    if (typeof item.inventoryQuantity === "number" && item.inventoryQuantity <= 0) return false;
    return true;
  };

  const findUnavailableSelections = (state) => {
    const unavailable = [];
    Object.keys(state.selections).forEach((type) => {
      const items = state.items[type] || [];
      items.forEach((item) => {
        if (state.selections[type]?.has(item.variantId) && !isItemAvailable(item)) {
          unavailable.push(item);
        }
      });
    });
    return unavailable;
  };

  const getSelectionSignature = (state) => {
    const signature = {
      fabric: Array.from(state.selections.fabric || []).sort(),
      mordant: Array.from(state.selections.mordant || []).sort(),
      dye: Array.from(state.selections.dye || []).sort(),
    };
    return JSON.stringify(signature);
  };

  const readLastAddedSignature = (state) => {
    try {
      const key = `bundleLastAdded:${state.bundleGroupKey}`;
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  };

  const writeLastAddedSignature = (state, signature) => {
    try {
      const key = `bundleLastAdded:${state.bundleGroupKey}`;
      window.localStorage.setItem(key, signature);
    } catch (error) {
      console.warn("Failed to persist bundle signature", error);
    }
  };

  const confirmDuplicateAdd = (confirmEl, confirmConfirmBtn, confirmCancelBtn) => {
    if (!confirmEl || !confirmConfirmBtn || !confirmCancelBtn) {
      return Promise.resolve(window.confirm("You've already added this exact bundle. Add it again?"));
    }

    return new Promise((resolve) => {
      const close = (result) => {
        confirmEl.classList.remove("is-open");
        confirmEl.setAttribute("aria-hidden", "true");
        confirmEl.setAttribute("hidden", "");
        confirmConfirmBtn.removeEventListener("click", onConfirm);
        confirmCancelBtn.removeEventListener("click", onCancel);
        confirmEl.removeEventListener("click", onBackdrop);
        resolve(result);
      };

      const onConfirm = () => close(true);
      const onCancel = () => close(false);
      const onBackdrop = (event) => {
        if (event.target === confirmEl) close(false);
      };

      confirmConfirmBtn.addEventListener("click", onConfirm);
      confirmCancelBtn.addEventListener("click", onCancel);
      confirmEl.addEventListener("click", onBackdrop);
      confirmEl.classList.add("is-open");
      confirmEl.setAttribute("aria-hidden", "false");
      confirmEl.removeAttribute("hidden");
      confirmConfirmBtn.focus();
    });
  };

  const fetchCart = async () => {
    const response = await fetch("/cart.js", {
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error("Cart fetch failed");
    return response.json();
  };

  const dispatchCartEvents = (cart, items) => {
    const payload = {
      cart,
      items,
      data: {
        itemCount: cart?.item_count ?? 0,
        source: "bundle-builder",
      },
    };

    [
      "theme:cart:update",
      "cart:update",
      "cart:updated",
      "cart:change",
      "cart:refresh",
      "cart:quantity:update",
      "us-minimum:refresh",
    ].forEach((eventName) => {
      document.dispatchEvent(new CustomEvent(eventName, { detail: payload }));
    });

    ["theme:cart:add", "cart:add"].forEach((eventName) => {
      document.dispatchEvent(new CustomEvent(eventName, { detail: payload }));
    });
  };

  const refreshCartUi = async (items) => {
    try {
      const cart = await fetchCart();
      dispatchCartEvents(cart, items);
      const cartDrawer = document.querySelector("cart-drawer-component");
      if (cartDrawer?.hasAttribute("auto-open")) {
        cartDrawer.open?.();
      }
    } catch (error) {
      console.warn("Bundle builder cart refresh failed", error);
    }
  };

  const collectSelectedItems = (state) => {
    const selected = [];
    Object.keys(state.selections).forEach((type) => {
      const items = state.items[type] || [];
      items.forEach((item) => {
        if (state.selections[type]?.has(item.variantId)) {
          selected.push(item);
        }
      });
    });
    return selected;
  };

  const validateSelections = (state) => {
    const rules = state.rules || { fabric: 1, mordant: 1, dye: 1 };
    if ((state.selections.fabric?.size || 0) < rules.fabric) {
      return { valid: false, message: state.texts?.validationFabric || "Please choose your fabric." };
    }
    if ((state.selections.mordant?.size || 0) < rules.mordant) {
      return { valid: false, message: state.texts?.validationMordant || "Please choose your mordant." };
    }
    if ((state.selections.dye?.size || 0) < rules.dye) {
      return { valid: false, message: state.texts?.validationDye || "Please choose your color." };
    }
    return { valid: true };
  };

  const collectCartItems = (state) => {
    const items = [];
    Object.keys(state.selections).forEach((type) => {
      state.selections[type].forEach((variantId) => {
        const id = Number(variantId);
        if (!Number.isFinite(id) || id <= 0) return;
        const properties = {
          "_bundle_type": type,
        };
        if (state.bundleGroupKey) properties["_bundle_group"] = state.bundleGroupKey;
        items.push({ id, quantity: 1, properties });
      });
    });
    if (state.instructionVariantId) {
      const id = Number(state.instructionVariantId);
      if (Number.isFinite(id) && id > 0) {
        const properties = { "_bundle_instruction": "true" };
        if (state.bundleGroupKey) properties["_bundle_group"] = state.bundleGroupKey;
        items.push({ id, quantity: 1, properties });
      }
    }
    return items;
  };

  const persistSelections = (state) => {
    try {
      const key = `bundleSelections:${state.bundleGroupKey}`;
      const payload = {
        fabric: state.selectionOrder.fabric?.length
          ? state.selectionOrder.fabric
          : Array.from(state.selections.fabric || []),
        mordant: state.selectionOrder.mordant?.length
          ? state.selectionOrder.mordant
          : Array.from(state.selections.mordant || []),
        dye: state.selectionOrder.dye?.length
          ? state.selectionOrder.dye
          : Array.from(state.selections.dye || []),
      };
      window.localStorage.setItem(key, JSON.stringify(payload));
    } catch (error) {
      console.warn("Failed to persist bundle selections", error);
    }
  };

  const restoreSelections = (state) => {
    try {
      const key = `bundleSelections:${state.bundleGroupKey}`;
      const raw = window.localStorage.getItem(key);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      Object.keys(state.selections).forEach((type) => {
        const values = Array.isArray(parsed[type]) ? parsed[type] : [];
        state.selections[type] = new Set(values);
        state.selectionOrder[type] = [...values];
      });
    } catch (error) {
      console.warn("Failed to restore bundle selections", error);
    }
  };

  const pushEvent = (event, state, data = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event,
      bundle_group: state.bundleGroupKey,
      discount: state.discountPercent,
      ...data,
    });
  };

  const formatToneLabel = (tone) => {
    switch (tone) {
      case "bright":
        return "Bright";
      case "warm":
        return "Warm";
      case "ferrous":
        return "Ferrous";
      default:
        return tone;
    }
  };

  const resolveDyeColor = (tone, colors) => {
    if (!colors) return "";
    if (tone === "ferrous") return colors.ferrous || colors.normal || colors.lactate || "";
    if (tone === "warm") return colors.lactate || colors.normal || colors.ferrous || "";
    return colors.normal || colors.lactate || colors.ferrous || "";
  };

  const getDyeColorForTone = (dye, tone) =>
    resolveDyeColor(tone, {
      normal: dye?.dyeColor,
      ferrous: dye?.dyeFerrousColor,
      lactate: dye?.dyeLactateColor,
    });

  const getActiveMordantTone = (state) => {
    const selectedMordant = getSelectedItem(state, "mordant");
    return selectedMordant?.mordantTone || "";
  };

  const updateDyeOptionColors = (modal, state) => {
    if (!modal) return;
    const tone = getActiveMordantTone(state);
    modal.querySelectorAll('[data-step="dye"] [data-variant-id]').forEach((option) => {
      const color = resolveDyeColor(tone, {
        normal: option.dataset.dyeColorNormal,
        ferrous: option.dataset.dyeColorFerrous,
        lactate: option.dataset.dyeColorLactate,
      });
      if (color) {
        option.style.setProperty("--dye-color", color);
      } else {
        option.style.removeProperty("--dye-color");
      }
    });
  };

  const updateModalTone = (modalDialog, state) => {
    if (!modalDialog) return;
    const selectedMordant = getSelectedItem(state, "mordant");
    const selectedDye = getSelectedItem(state, "dye");
    const baseColor = getDyeColorForTone(selectedDye, selectedMordant?.mordantTone);
    if (!baseColor) {
      modalDialog.style.removeProperty("--bundle-color");
      modalDialog.style.removeProperty("--bundle-color-soft");
      return;
    }
    const softened = mixColor(baseColor, "#f6f1eb", 0.35);
    modalDialog.style.setProperty("--bundle-color", baseColor);
    modalDialog.style.setProperty("--bundle-color-soft", softened);
  };

  const getSelectedItem = (state, type) => {
    const id = state.selectionOrder[type]?.[state.selectionOrder[type].length - 1]
      || Array.from(state.selections[type] || [])[0];
    if (!id) return null;
    return (state.items[type] || []).find((item) => String(item.variantId) === String(id));
  };

  const mixColor = (color, other, ratio) => {
    const a = hexToRgb(color);
    const b = hexToRgb(other);
    if (!a || !b) return color;
    const mix = {
      r: Math.round(a.r + (b.r - a.r) * ratio),
      g: Math.round(a.g + (b.g - a.g) * ratio),
      b: Math.round(a.b + (b.b - a.b) * ratio),
    };
    return rgbToHex(mix);
  };

  const hexToRgb = (hex) => {
    const cleaned = String(hex).replace("#", "");
    if (cleaned.length !== 6) return null;
    const num = parseInt(cleaned, 16);
    return {
      r: (num >> 16) & 255,
      g: (num >> 8) & 255,
      b: num & 255,
    };
  };

  const rgbToHex = ({ r, g, b }) => {
    const toHex = (value) => value.toString(16).padStart(2, "0");
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  const trapFocus = (modal) => {
    const focusable = modal.querySelectorAll(
      "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const handle = (event) => {
      if (event.key !== "Tab") return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    modal.addEventListener("keydown", handle);
    modal.dataset.focusTrap = "true";
    modal.dataset.focusTrapHandler = handle;
    first.focus();
  };

  const releaseFocus = (modal) => {
    if (modal.dataset.focusTrap === "true") {
      const handler = modal.dataset.focusTrapHandler;
      if (handler) modal.removeEventListener("keydown", handler);
      delete modal.dataset.focusTrap;
      delete modal.dataset.focusTrapHandler;
    }
  };

  document.addEventListener("DOMContentLoaded", init);
})();
